// pms-core / domain — « DEMANDES SANS SUITE » (PB9, PMS-PHONE-BOOKING-BRIEF §2.1, Q8).
//
// Pure. A call that ends without a booking is counted, anonymously: the dates
// asked, the party's age bands, and the offer and price given, if any. Nothing
// here — nor in `pms_turnaway` — can name a person: no guest, no contact, no
// staff member. `kind` is derived, never asked: `denial` when nothing could be
// quoted for the party, `regret` when an offer was given and declined.

import type { AgeBand, Cents, IsoDate, RatePlanId, RoomTypeId } from './types.js';

export type TurnawayKind = 'denial' | 'regret';

/** The offer the caller heard: the category sold, its plan, the stay's total. */
export interface TurnawayQuote {
  roomTypeId: RoomTypeId;
  ratePlanId: RatePlanId;
  totalCents: Cents;
}

/** What the desk records when it closes the call (« Nouvel appel », PB5). */
export interface TurnawayInput {
  propertyCode: string;
  /** The desk's phone; the website could count its own later. */
  channel?: 'phone' | 'web';
  arrival: IsoDate;
  nights: number;
  /** One band per person, as asked in step 2 — at least one adult. */
  persons: AgeBand[];
  /** null: nothing could be sold for the party — a denial. */
  quoted: TurnawayQuote | null;
}

export interface Turnaway extends Required<Omit<TurnawayInput, 'channel'>> {
  id: number;
  recordedAt: string;
  channel: 'phone' | 'web';
  kind: TurnawayKind;
}

export const turnawayKind = (quoted: TurnawayQuote | null): TurnawayKind => (quoted === null ? 'denial' : 'regret');

const BANDS: readonly AgeBand[] = ['adult', 'child_13_17', 'child_9_12', 'child_6_8', 'child_3_5', 'child_unknown', 'infant'];

/** The record, as one French sentence or null — the mirror of `pms_turnaway`'s CHECKs. */
export function turnawayProblem(t: TurnawayInput): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t.arrival) || Number.isNaN(Date.parse(t.arrival))) return 'Date d’arrivée invalide.';
  if (!Number.isInteger(t.nights) || t.nights < 1 || t.nights > 60) return 'De 1 à 60 nuits.';
  if (t.persons.length < 1 || t.persons.length > 12) return 'De 1 à 12 personnes.';
  if (t.persons.some((b) => !BANDS.includes(b))) return 'Tranche d’âge invalide.';
  if (!t.persons.includes('adult')) return 'Au moins un adulte.';
  if (t.quoted !== null && (!Number.isInteger(t.quoted.totalCents) || t.quoted.totalCents < 0)) {
    return 'Prix proposé invalide.';
  }
  return null;
}

/** The month's count, as Réservations shows it. */
export interface TurnawaySummary {
  total: number;
  denial: number;
  regret: number;
  /** What the declined offers were worth — the price the caller heard, summed. */
  regretCents: Cents;
  /** The dates asked, earliest arrival first. */
  stays: { arrival: IsoDate; nights: number; persons: number; kind: TurnawayKind }[];
}

export function turnawaySummary(rows: readonly Turnaway[]): TurnawaySummary {
  const regrets = rows.filter((r) => r.kind === 'regret');
  return {
    total: rows.length,
    denial: rows.length - regrets.length,
    regret: regrets.length,
    regretCents: regrets.reduce((s, r) => s + (r.quoted?.totalCents ?? 0), 0),
    stays: rows
      .map((r) => ({ arrival: r.arrival, nights: r.nights, persons: r.persons.length, kind: r.kind }))
      .sort((a, b) => a.arrival.localeCompare(b.arrival) || a.nights - b.nights),
  };
}
